import React from 'react';
import { App, Alert, Button, theme } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { AppDispatch } from '../../../reduxTypes';
import { useEvent } from '../../events/hooks/useEvent';
import { useCurrentPlan, usePrerenderUserId } from '../../../hooks/usePrerenderUser';
import { billingApiLegacySlice, useLazyGetCollectNowLinkQuery } from '../api/billingApiLegacySlice';
import { useUnpaidInvoices } from '../hooks/useUnpaidInvoices';

// Pay now cannot tell whether the payment page opened: with 'noreferrer', window.open always returns null.
export const PAYMENT_PENDING_TTL_MS = 30 * 60 * 1000;

const paymentPendingKey = (userId: number) => `billing.unpaidInvoices.paymentPending.${userId}`;

// sessionStorage access throws when the browser blocks site data; losing the flag only skips the refetch on return.
function withSessionStorage<T>(access: (storage: Storage) => T, fallback: T): T {
  try {
    return access(sessionStorage);
  } catch {
    return fallback;
  }
}

const isPaymentPending = (userId: number): boolean =>
  withSessionStorage((storage) => {
    const payNowClickedAt = Number(storage.getItem(paymentPendingKey(userId)));
    return Date.now() - payNowClickedAt < PAYMENT_PENDING_TTL_MS;
  }, false);
const rememberPaymentPending = (userId: number): void =>
  withSessionStorage((storage) => storage.setItem(paymentPendingKey(userId), String(Date.now())), undefined);
const forgetPaymentPending = (userId: number): void =>
  withSessionStorage((storage) => storage.removeItem(paymentPendingKey(userId)), undefined);

function UnpaidInvoicesBanner() {
  const { message } = App.useApp();
  const userPlan = useCurrentPlan();
  const userId = usePrerenderUserId();
  const { unpaidInvoices, isSubscriptionLoaded } = useUnpaidInvoices();
  const navigate = useNavigate();
  const [getCollectNowLink, payNowResult] = useLazyGetCollectNowLinkQuery();
  const dispatch = useDispatch<AppDispatch>();
  const { track } = useEvent();
  const { token } = theme.useToken();

  const hasUnpaidInvoices = !!unpaidInvoices;
  const areDuesPaid = isSubscriptionLoaded && !hasUnpaidInvoices;

  React.useEffect(() => {
    if (areDuesPaid) forgetPaymentPending(userId);
  }, [areDuesPaid, userId]);

  React.useEffect(() => {
    if (!hasUnpaidInvoices) return undefined;

    const refetchSubscriptionOnReturnFromPayment = () => {
      if (isPaymentPending(userId)) dispatch(billingApiLegacySlice.util.invalidateTags(['Subscription']));
    };
    window.addEventListener('focus', refetchSubscriptionOnReturnFromPayment);
    return () => window.removeEventListener('focus', refetchSubscriptionOnReturnFromPayment);
  }, [hasUnpaidInvoices, userId, dispatch]);

  if (!unpaidInvoices) return null;

  const onPayNowClick = () => {
    getCollectNowLink()
      .unwrap()
      .then((response) => {
        window.open(response.result.collectNowLink, '_blank', 'noreferrer');
        rememberPaymentPending(userId);
      })
      .catch(() => {
        message.error('Cannot generate payment link, please try again later.');
      });
    track('Due Invoice Payment Started', {
      subscription_plan: userPlan.name,
      overdue_amount: unpaidInvoices.totalDues,
      location: 'global_banner',
    });
  };

  return (
    <Alert
      showIcon
      style={{ margin: token.margin }}
      type="warning"
      message={unpaidInvoices.title}
      description="There are unpaid invoices on your account. Please settle outstanding invoices to maintain your service.
          Questions? Click on 'Help' button, bottom left."
      action={
        unpaidInvoices.hasHierarchies ? (
          <Button type="primary" onClick={() => navigate('/billing')}>
            Go to Billing
          </Button>
        ) : (
          <Button type="primary" onClick={onPayNowClick} loading={payNowResult.isFetching}>
            Pay now
          </Button>
        )
      }
    />
  );
}

export default UnpaidInvoicesBanner;
